import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';
import { Save, X as Cancel, Edit, Plus } from 'lucide-react';
import { TreatmentRecord, TreatmentRecordInput } from '../../types/treatment';
import { BasicInfoSection } from './TreatmentForm/BasicInfoSection';
import { PhysicalConditionSection } from './TreatmentForm/PhysicalConditionSection';
import { TreatmentDetailsSection } from './TreatmentForm/TreatmentDetailsSection';
import { CounselingSection } from './TreatmentForm/CounselingSection';
import { NotesSection } from './TreatmentForm/NotesSection';

interface TreatmentManagerProps {
  customerId: number;
  mode?: 'view' | 'edit' | 'create';
  initialData?: Partial<TreatmentRecord>;
  onSave?: (data: TreatmentRecordInput) => Promise<void>;
  onCancel?: () => void;
  onModeChange?: (mode: 'view' | 'edit' | 'create') => void;
}


export const TreatmentManager: React.FC<TreatmentManagerProps> = ({
  customerId,
  mode = 'view',
  initialData,
  onSave,
  onCancel,
  onModeChange,
}) => {
  const [formData, setFormData] = useState<Partial<TreatmentRecord>>({
    customerId,
    physicalCondition: '普通',
    symptoms: [],
    techniques: [],
    focusAreas: [],
    notes: {
      before: '',
      during: '',
      after: '',
      conversation: '',
    },
    lifestyle: {},
    ...initialData,
  });
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 自動保存機能
  useEffect(() => {
    if (mode !== 'edit' && mode !== 'create') return;

    const timer = setTimeout(() => {
      setAutoSaveStatus('saving');
      // ここで実際の自動保存処理を実装
      // 今は擬似的に保存完了を表示
      setTimeout(() => {
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      }, 500);
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData, mode]);

  const handleFieldChange = (updates: Partial<TreatmentRecord>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };


  const handleSubmit = async () => {
    if (!onSave) return;

    setIsSubmitting(true);

    try {
      // バリデーション
      if (!formData.treatmentMenu) {
        throw new Error('施術メニューを選択してください');
      }
      if (!formData.treatmentDuration) {
        throw new Error('施術時間を入力してください');
      }

      // 保存処理
      await onSave(formData as TreatmentRecordInput);
      toast.success('保存しました');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存中にエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex justify-end items-center gap-2">
        {mode === 'view' && onModeChange && (
          <>
            <Button
              variant="outline"
              onClick={() => onModeChange('create')}
            >
              <Plus className="mr-2 h-4 w-4" />
              新規作成
            </Button>
            <Button
              onClick={() => onModeChange('edit')}
            >
              <Edit className="mr-2 h-4 w-4" />
              編集
            </Button>
          </>
        )}
        {mode !== 'view' && (
          <>
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <Cancel className="mr-2 h-4 w-4" />
              キャンセル
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  保存中...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  保存
                </>
              )}
            </Button>
          </>
        )}
      </div>

      <Separator />

      {/* 入力フォーム */}
      <div className="grid grid-cols-1 gap-6">
        <div className="col-span-1">
          <BasicInfoSection
            record={formData}
            onChange={handleFieldChange}
            mode={mode}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PhysicalConditionSection
            record={formData}
            onChange={handleFieldChange}
            mode={mode}
          />

          <TreatmentDetailsSection
            record={formData}
            onChange={handleFieldChange}
            mode={mode}
          />
        </div>

        <div className="col-span-1">
          <CounselingSection
            record={formData}
            onChange={handleFieldChange}
            mode={mode}
          />
        </div>

        <div className="col-span-1">
          <NotesSection
            notes={formData.notes || { before: '', during: '', after: '', conversation: '' }}
            onChange={(notes) => handleFieldChange({ notes })}
            autoSaveStatus={autoSaveStatus}
            disabled={mode === 'view'}
          />
        </div>
      </div>
    </div>
  );
};